import React, { useEffect, useMemo, useState } from "react";
import { api, fmtUSD, fmtCompact, fmtPct } from "../lib/api";
import {
  Handshake, Brain, Sparkle, Lightning, ChartLineUp, ShieldCheck, Users, PaperPlaneRight,
  ArrowsClockwise, Flag, ThumbsUp, Trophy, ListChecks,
} from "@phosphor-icons/react";

/* ────────────────────────────────────────────────────────────────────────────
 * Strategy Multiplier · Partnership Doctrine
 *   - Force-Multiplier banner (Team of N → Capacity of M)
 *   - Competitive Advantage Delta vs Industry Benchmarks
 *   - Strategic Move proposal form (collaborative dials, never shocks)
 *   - Harmony Impact dashboard (6 dimensions)
 *   - Amplification Plays the Agent Army can auto-trigger on approval
 *   - Joint Reasoning thread (Strategic Partner ↔ Human)
 * ────────────────────────────────────────────────────────────────────────── */

const PARAM_FIELDS = [
  { key: "domestic_shift_pct",         label: "Domestic Sourcing Shift",      unit: "%",      min: -50, max: 50, step: 1 },
  { key: "supplier_consolidation_pct", label: "Supplier Consolidation",       unit: "%",      min: -50, max: 50, step: 1 },
  { key: "horizon_shift_months",       label: "Contract Horizon Shift",       unit: "months", min: -24, max: 24, step: 1 },
  { key: "volume_commitment_pct",      label: "Volume Commitment",            unit: "%",      min:   0, max: 100, step: 5 },
];

// Competitive Advantage Delta — Procureo vs traditional mid-market benchmark.
const ADVANTAGE = [
  { key: "decision_velocity", label: "Decision Velocity",    industry: 1,   procureo: 8,   unit: "x", desc: "Strategy turn-time vs traditional category review." },
  { key: "coverage_ratio",    label: "Supplier Coverage",     industry: 10,  procureo: 60,  unit: "/analyst", desc: "Suppliers actively monitored per category lead." },
  { key: "risk_adjudication", label: "Risk Adjudication",     industry: 14,  procureo: 1,   unit: " days", desc: "Time from 10-K signal to risk-score update.", invert: true },
  { key: "cycle_compression", label: "Sourcing Cycle Compression", industry: 90, procureo: 22, unit: " days", desc: "Cycle from RFI to award.", invert: true },
];

export default function StrategyMultiplierView() {
  const [baseline, setBaseline] = useState(null);
  const [moves, setMoves] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const refreshList = () =>
    api.strategyMoves().then((d) => setMoves(d.moves || [])).catch(() => setMoves([]));

  useEffect(() => {
    api.strategyMultiplier().then(setBaseline).catch(() => setBaseline(null));
    refreshList();
  }, []);

  const active = useMemo(
    () => (moves || []).find((m) => m.id === activeId) || null,
    [moves, activeId],
  );

  if (!baseline || !moves) return <Skeleton />;

  return (
    <section className="bg-slate-900 border border-slate-800" data-testid="strategy-multiplier-view">
      <Header
        fm={baseline.force_multiplier}
        onAdd={() => { setShowForm(true); setActiveId(null); }}
      />

      <AdvantageBand />

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-px bg-slate-800">
        {/* LEFT · Move list / form */}
        <aside className="xl:col-span-3 bg-slate-950 p-4 space-y-3 min-h-[480px]">
          <div className="flex items-center justify-between">
            <span className="tracking-mono-label text-[10px] text-slate-500">Strategic Moves</span>
            <span className="font-mono text-[10px] text-slate-500">{moves.length}</span>
          </div>
          <ul className="space-y-1.5 max-h-[420px] overflow-auto thin-scroll" data-testid="strategy-move-list">
            {moves.map((m) => (
              <li key={m.id}>
                <button
                  data-testid={`strategy-move-${m.id}`}
                  onClick={() => { setActiveId(m.id); setShowForm(false); }}
                  className={`w-full text-left p-2.5 rounded-sm border transition-colors ${
                    activeId === m.id
                      ? "border-emerald-500/50 bg-emerald-500/10"
                      : "border-slate-800 hover:border-slate-700 hover:bg-slate-900"
                  }`}
                >
                  <div className="text-xs text-slate-100 truncate">{m.title}</div>
                  <div className="font-mono text-[10px] text-slate-500 truncate">
                    {m.author} · {new Date(m.created_at).toLocaleDateString()}
                  </div>
                  <div className="font-mono text-[10px] text-emerald-300 mt-0.5">
                    net {m.impact?.net_value_usd ? fmtCompact(m.impact.net_value_usd) : "—"} · resilience {fmtPct(m.impact?.resilience_pct || 0, 1)}
                  </div>
                </button>
              </li>
            ))}
            {moves.length === 0 && (
              <li className="text-[11px] font-mono text-slate-500 text-center py-6">
                No moves yet. Propose one to engage the co-pilot.
              </li>
            )}
          </ul>
        </aside>

        {/* RIGHT · Form OR Active Move detail */}
        <main className="xl:col-span-9 bg-slate-950 min-h-[480px]">
          {showForm
            ? <ProposeMoveForm onSubmitted={(m) => { refreshList().then(() => setActiveId(m.id)); setShowForm(false); }} onCancel={() => setShowForm(false)} />
            : active
              ? <MoveDetail move={active} onRefresh={(updated) => {
                  setMoves((prev) => prev.map((x) => x.id === updated.id ? updated : x));
                }} />
              : <EmptyState onAdd={() => setShowForm(true)} /> }
        </main>
      </div>
    </section>
  );
}

/* ─── Header · Unified Operating Hub + Force-Multiplier ────────────────── */
function Header({ fm, onAdd }) {
  return (
    <header className="px-5 py-4 border-b border-slate-800 flex items-start justify-between gap-4 flex-wrap">
      <div>
        <h2 className="font-display text-base font-semibold text-slate-50 flex items-center gap-2">
          <Handshake weight="duotone" className="text-emerald-400" size={16} />
          Strategy Multiplier · Unified Operating Hub
        </h2>
        <p className="text-xs text-slate-500 mt-0.5 font-mono">
          As your Strategic Partner, propose a Strategic Move · Strategic Partner returns Harmony Impact · the Hub synchronizes around the Strategic Lead.
        </p>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <StrategyLockIndicator />
        <ForceMultiplierBadge fm={fm} />
        <button
          data-testid="propose-strategic-move"
          onClick={onAdd}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-sm border border-emerald-500/40 bg-emerald-500/10
                     text-emerald-200 font-mono text-xs tracking-mono-label hover:bg-emerald-500/20 transition-colors"
        >
          <Sparkle weight="bold" size={12} /> propose strategic move
        </button>
      </div>
    </header>
  );
}

// Competitive Advantage Indicator — quantifies the head-start the COP delivers.
function StrategyLockIndicator() {
  return (
    <div
      data-testid="strategy-lock-indicator"
      className="flex items-center gap-2 px-3 py-2 rounded-sm border border-amber-500/40 bg-amber-500/5"
      title="LME Copper trend + Terminal Risk Score + ECI labor index fused into a 14-day head-start vs market consensus."
    >
      <Trophy weight="fill" className="text-amber-400" size={16} />
      <div className="leading-tight">
        <div className="tracking-mono-label text-[10px] text-amber-200">
          Strategy Lock · 14 days ahead of market
        </div>
        <div className="font-mono text-[10px] text-amber-300/80">
          LME · BLS ECI · Terminal Risk · fused
        </div>
      </div>
    </div>
  );
}

function ForceMultiplierBadge({ fm }) {
  if (!fm) return null;
  return (
    <div
      data-testid="force-multiplier-badge"
      className="flex items-center gap-3 px-4 py-2 rounded-sm border border-emerald-500/40 bg-emerald-500/5"
    >
      <Users weight="duotone" className="text-emerald-400" size={20} />
      <div className="leading-tight">
        <div className="tracking-mono-label text-[10px] text-emerald-300">
          Force Multiplier · {fm.multiplier}× leverage
        </div>
        <div className="font-mono text-[11px] text-emerald-100">
          Team of {fm.team_size} performing at the capacity of {fm.effective_capacity} analysts
        </div>
      </div>
    </div>
  );
}

/* ─── Competitive Advantage Delta band ──────────────────────────────────── */
function AdvantageBand() {
  return (
    <section className="border-b border-slate-800 px-5 py-4 bg-slate-950/60" data-testid="advantage-delta">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="font-display text-sm font-semibold text-slate-100 flex items-center gap-2">
            <Trophy weight="duotone" className="text-amber-400" size={14} />
            Competitive Advantage Delta
          </div>
          <p className="text-[11px] text-slate-500 font-mono mt-0.5">
            Procureo vs traditional mid-market benchmark · structural advantage we build together.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-800">
        {ADVANTAGE.map((a) => {
          // For invert metrics (days), lower is better → invert the bar direction.
          const ratio = a.invert ? a.industry / a.procureo : a.procureo / a.industry;
          const headline = a.invert
            ? `${(a.industry / a.procureo).toFixed(1)}× faster`
            : `${(a.procureo / a.industry).toFixed(1)}× ${a.unit === "/analyst" ? "broader" : "sharper"}`;
          // Bar widths (cap 100%)
          const procureoPct = Math.min(100, ratio >= 1 ? 100 : ratio * 100);
          const industryPct = Math.min(100, ratio >= 1 ? (1 / ratio) * 100 : 100);
          return (
            <div key={a.key} className="bg-slate-950 p-4" data-testid={`advantage-${a.key}`}>
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-xs text-slate-100 font-medium">{a.label}</span>
                <span className="font-mono text-[10px] text-amber-300">{headline}</span>
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5 font-mono">{a.desc}</div>
              <div className="mt-3 space-y-1.5">
                <BarRow label="Procureo" value={`${a.procureo}${a.unit}`} pct={procureoPct} accent="emerald" />
                <BarRow label="Industry" value={`${a.industry}${a.unit}`} pct={industryPct} accent="slate" />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function BarRow({ label, value, pct, accent }) {
  const bar = accent === "emerald" ? "bg-emerald-500/70" : "bg-slate-600";
  const fg = accent === "emerald" ? "text-emerald-300" : "text-slate-400";
  return (
    <div className="flex items-center gap-2">
      <span className={`tracking-mono-label text-[9px] w-14 ${fg}`}>{label}</span>
      <div className="flex-1 h-1.5 bg-slate-800 overflow-hidden rounded-sm">
        <div className={`h-full ${bar}`} style={{ width: `${Math.max(4, pct)}%` }} />
      </div>
      <span className={`font-mono text-[10px] w-16 text-right ${fg}`}>{value}</span>
    </div>
  );
}

/* ─── Propose Strategic Move form ────────────────────────────────────────── */
function ProposeMoveForm({ onSubmitted, onCancel }) {
  const [title, setTitle] = useState("");
  const [intent, setIntent] = useState("");
  const [author, setAuthor] = useState("Cameron · Category Lead");
  const [params, setParams] = useState({
    domestic_shift_pct: 15,
    supplier_consolidation_pct: -10,
    horizon_shift_months: 6,
    volume_commitment_pct: 70,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e?.preventDefault();
    if (!title.trim()) { setError("Give the move a title."); return; }
    setSubmitting(true);
    setError(null);
    try {
      const move = await api.createStrategyMove({
        title, intent, author,
        scope: { type: "general", value: "", label: "Portfolio-wide" },
        parameters: params,
      });
      onSubmitted?.(move);
    } catch (e) {
      setError(e?.message || "Forecast failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="p-5 space-y-4" data-testid="propose-move-form">
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <h3 className="font-display text-sm font-semibold text-slate-50 flex items-center gap-2">
            <Sparkle weight="duotone" className="text-emerald-400" size={14} />
            Propose a Strategic Move
          </h3>
          <p className="text-[11px] text-slate-500 font-mono mt-0.5">
            Operator dials · Strategic Partner returns Harmony Impact + Amplification Plays · we engage as a single team.
          </p>
        </div>
        <button type="button" onClick={onCancel}
          className="font-mono text-[10px] tracking-mono-label text-slate-400 hover:text-slate-200">
          ✕ cancel
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input data-testid="move-title" label="Title" placeholder="e.g. Shift 15% of components to domestic sourcing"
               value={title} onChange={(v) => setTitle(v)} />
        <Input data-testid="move-author" label="Author" value={author} onChange={(v) => setAuthor(v)} />
      </div>

      <Textarea data-testid="move-intent" label="Strategic intent" rows={3}
                placeholder="What outcome are we steering toward? Resilience, cost, cycle compression, all three?"
                value={intent} onChange={setIntent} />

      <div className="border border-slate-800 rounded-sm p-3 bg-slate-950">
        <div className="tracking-mono-label text-[10px] text-slate-500 mb-3">Move parameters · scenario discovery dials</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {PARAM_FIELDS.map((f) => (
            <div key={f.key} data-testid={`param-${f.key}`}>
              <div className="flex items-baseline justify-between">
                <label className="text-[11px] text-slate-300">{f.label}</label>
                <span className="font-mono text-[11px] text-emerald-300">
                  {(params[f.key] >= 0 && f.min < 0) ? "+" : ""}{params[f.key]}{f.unit}
                </span>
              </div>
              <input
                data-testid={`param-${f.key}-input`}
                type="range" min={f.min} max={f.max} step={f.step} value={params[f.key]}
                onChange={(e) => setParams({ ...params, [f.key]: +e.target.value })}
                className="w-full mt-1"
              />
            </div>
          ))}
        </div>
      </div>

      {error && <div className="text-rose-300 text-xs font-mono" data-testid="propose-error">{error}</div>}

      <div className="flex items-center gap-3">
        <button
          data-testid="submit-strategic-move"
          type="submit" disabled={submitting}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-sm border border-emerald-500/40 bg-emerald-500/10
                     text-emerald-200 font-mono text-xs tracking-mono-label hover:bg-emerald-500/20 disabled:opacity-50"
        >
          {submitting
            ? <><ArrowsClockwise size={12} className="animate-spin" /> strategic partner forecasting…</>
            : <><Brain weight="duotone" size={14} /> engage co-pilot</>}
        </button>
        <span className="text-[10px] font-mono text-slate-500">
          Strategic Partner will project Harmony Impact + Amplification Plays + seed the Joint Reasoning thread.
        </span>
      </div>
    </form>
  );
}

function Input({ label, value, onChange, placeholder, ...rest }) {
  return (
    <label className="block">
      <span className="tracking-mono-label text-[10px] text-slate-500">{label}</span>
      <input
        {...rest}
        type="text" value={value} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 block w-full bg-slate-950 border border-slate-800 rounded-sm px-2.5 py-1.5
                   text-sm text-slate-100 placeholder:text-slate-600 focus:border-emerald-500/60 outline-none"
      />
    </label>
  );
}
function Textarea({ label, value, onChange, rows = 3, placeholder, ...rest }) {
  return (
    <label className="block">
      <span className="tracking-mono-label text-[10px] text-slate-500">{label}</span>
      <textarea
        {...rest}
        rows={rows} value={value} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 block w-full bg-slate-950 border border-slate-800 rounded-sm px-2.5 py-1.5
                   text-sm text-slate-100 placeholder:text-slate-600 focus:border-emerald-500/60 outline-none"
      />
    </label>
  );
}

/* ─── Active Move Detail ─────────────────────────────────────────────────── */
function MoveDetail({ move, onRefresh }) {
  return (
    <div className="p-5 space-y-5" data-testid={`move-detail-${move.id}`}>
      <div>
        <h3 className="font-display text-base font-semibold text-slate-50">{move.title}</h3>
        <p className="text-xs text-slate-300 mt-1">{move.intent}</p>
        <p className="text-[10px] text-slate-500 font-mono mt-1">
          {move.author} · {new Date(move.created_at).toLocaleString()}
        </p>
      </div>

      {/* Strategic Partner headline */}
      <div className="border border-fuchsia-500/30 bg-fuchsia-500/5 rounded-sm p-4">
        <div className="flex items-center gap-2 mb-2">
          <Brain weight="duotone" className="text-fuchsia-400" size={16} />
          <span className="tracking-mono-label text-[10px] text-fuchsia-300">
            Strategic Partner Verdict · Co-Pilot Headline
          </span>
        </div>
        <p className="text-slate-100 leading-snug" data-testid="move-headline">{move.impact?.headline}</p>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-800 rounded-sm overflow-hidden">
          <Stat label="Net Value Δ" value={fmtUSD(move.impact?.net_value_usd || 0)} accent={(move.impact?.net_value_usd || 0) >= 0 ? "emerald" : "rose"} />
          <Stat label="Resilience Δ" value={fmtPct(move.impact?.resilience_pct || 0, 1)} accent={(move.impact?.resilience_pct || 0) >= 0 ? "emerald" : "rose"} />
          <Stat label="Effort Saved" value={`${move.amplification_plays?.total_effort_saved_hours || 0} hr`} accent="emerald" />
        </div>
      </div>

      {/* Harmony Impact across 6 dimensions */}
      <HarmonyGrid impact={move.impact} />

      {/* Amplification Plays */}
      <AmplificationPlays plays={move.amplification_plays} />

      {/* Joint Reasoning thread */}
      <JointReasoning move={move} onRefresh={onRefresh} />
    </div>
  );
}

function HarmonyGrid({ impact }) {
  const items = impact?.harmony_impact || [];
  if (items.length === 0) return null;
  return (
    <div data-testid="harmony-impact-grid">
      <div className="tracking-mono-label text-[10px] text-emerald-300 mb-2 flex items-center gap-2">
        <ListChecks weight="duotone" size={12} />
        Harmony Impact · six dimensions, equally weighted
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-800">
        {items.map((i, idx) => {
          const fg = i.sentiment === "FAVORABLE" ? "text-emerald-300"
            : i.sentiment === "CONCERN" ? "text-rose-300" : "text-slate-300";
          const bd = i.sentiment === "FAVORABLE" ? "border-emerald-500/40"
            : i.sentiment === "CONCERN" ? "border-rose-500/40" : "border-slate-700";
          return (
            <div key={idx} className={`bg-slate-950 p-3 border-l-2 ${bd}`}
                 data-testid={`harmony-${i.dimension?.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-xs text-slate-100">{i.dimension}</span>
                <span className={`font-mono text-[10px] tracking-mono-label ${fg}`}>{i.sentiment}</span>
              </div>
              <div className={`font-mono text-lg mt-1 ${fg}`}>{i.delta_label || `${i.delta_value}${i.delta_unit || ""}`}</div>
              <p className="text-[11px] text-slate-400 leading-snug mt-1">{i.rationale}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AmplificationPlays({ plays }) {
  const list = plays?.plays || [];
  if (list.length === 0) return null;
  return (
    <div data-testid="amplification-plays">
      <div className="tracking-mono-label text-[10px] text-emerald-300 mb-2 flex items-center gap-2">
        <Lightning weight="duotone" size={12} />
        Strategic Plays · your Strategic Partner stands ready
      </div>
      <p className="text-[11px] text-slate-400 mb-2 italic">{plays?.productivity_narrative}</p>
      <ul className="space-y-2">
        {list.map((p, i) => (
          <li key={i} data-testid={`play-${i}`}
              className="border border-emerald-500/30 bg-emerald-500/5 rounded-sm p-3">
            <div className="flex items-baseline justify-between gap-2 mb-1">
              <span className="text-slate-100 text-sm font-medium">{p.title}</span>
              <span className="font-mono text-[10px] text-emerald-300">+{p.effort_saved_hours} hr saved</span>
            </div>
            <p className="text-[12px] text-slate-300 leading-snug">{p.action}</p>
            <div className="mt-2 flex items-center gap-2 flex-wrap text-[10px] font-mono">
              <span className="text-fuchsia-300 px-1.5 py-0.5 rounded-sm border border-fuchsia-500/40">
                <Brain size={9} weight="bold" className="inline mr-1" /> {p.agent}
              </span>
              <span className="text-slate-400 truncate flex-1">{p.target_set}</span>
              <button
                data-testid={`approve-play-${i}`}
                onClick={() => alert(`Approved: ${p.title}\n\nThe Agent Army will engage on this play.`)}
                className="inline-flex items-center gap-1 px-2 py-0.5 border border-emerald-500/40
                           text-emerald-200 rounded-sm hover:bg-emerald-500/10 tracking-mono-label"
              >
                <ThumbsUp size={10} weight="bold" /> approve & engage
              </button>
            </div>
            {p.expected_outcome && (
              <div className="mt-1.5 text-[10px] font-mono text-slate-500 border-l-2 border-slate-700 pl-2">
                expected outcome · {p.expected_outcome}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function JointReasoning({ move, onRefresh }) {
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const turns = move.dialogue || [];
  const submit = async () => {
    if (!draft.trim()) return;
    setSending(true);
    try {
      await api.addDialogueTurn(move.id, { author: move.author, content: draft });
      const fresh = await api.getStrategyMove(move.id);
      onRefresh?.(fresh);
      setDraft("");
    } finally { setSending(false); }
  };
  return (
    <div data-testid="joint-reasoning">
      <div className="tracking-mono-label text-[10px] text-emerald-300 mb-2 flex items-center gap-2">
        <Handshake weight="duotone" size={12} />
        Joint Reasoning · shared cognition with the Strategic Partner
      </div>
      <ul className="space-y-2 max-h-[320px] overflow-auto thin-scroll bg-slate-950 border border-slate-800 rounded-sm p-3">
        {turns.length === 0 && (
          <li className="text-[11px] font-mono text-slate-500 text-center py-4">
            Strategic Partner is awaiting your first observation.
          </li>
        )}
        {turns.map((t, i) => (
          <li key={i} data-testid={`turn-${i}`} className={`p-2 rounded-sm border-l-2 ${
            t.actor === "OPUS"
              ? "border-fuchsia-500/40 bg-fuchsia-500/5"
              : "border-emerald-500/40 bg-emerald-500/5"
          }`}>
            <div className="flex items-baseline justify-between mb-1">
              <span className={`font-mono text-[10px] tracking-mono-label ${
                t.actor === "OPUS" ? "text-fuchsia-300" : "text-emerald-300"
              }`}>
                {t.actor === "OPUS"
                  ? <><Brain size={9} weight="bold" className="inline mr-1" /> STRATEGIC PARTNER · {t.role}</>
                  : <><Flag size={9} weight="bold" className="inline mr-1" /> {(t.author || "HUMAN").toUpperCase()}</>}
              </span>
              {t.ts && <span className="font-mono text-[9px] text-slate-600">{new Date(t.ts).toLocaleTimeString()}</span>}
            </div>
            <p className="text-xs text-slate-100 leading-snug">{t.content}</p>
            {t.next_prompt && (
              <p className="text-[10px] text-slate-500 font-mono mt-1 italic">↳ {t.next_prompt}</p>
            )}
          </li>
        ))}
      </ul>
      <div className="mt-2 flex items-end gap-2">
        <textarea
          data-testid="dialogue-input"
          value={draft} onChange={(e) => setDraft(e.target.value)}
          rows={2} placeholder="Refine the move, surface a constraint, or invite Strategic Partner to push back…"
          className="flex-1 bg-slate-950 border border-slate-800 rounded-sm px-2.5 py-1.5 text-xs text-slate-100
                     placeholder:text-slate-600 focus:border-emerald-500/60 outline-none resize-none"
        />
        <button
          data-testid="send-turn" onClick={submit} disabled={sending || !draft.trim()}
          className="inline-flex items-center gap-1 px-3 py-2 border border-emerald-500/40 bg-emerald-500/10
                     text-emerald-200 rounded-sm font-mono text-[10px] tracking-mono-label hover:bg-emerald-500/20 disabled:opacity-50"
        >
          {sending ? <ArrowsClockwise size={11} className="animate-spin" /> : <PaperPlaneRight size={11} weight="bold" />}
          send
        </button>
      </div>
    </div>
  );
}

function Stat({ label, value, accent = "slate" }) {
  const color = { slate: "text-slate-100", emerald: "text-emerald-300", rose: "text-rose-300" }[accent];
  return (
    <div className="bg-slate-950 p-3">
      <div className="tracking-mono-label text-[10px] text-slate-500">{label}</div>
      <div className={`font-mono text-base ${color} mt-1`}>{value}</div>
    </div>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div className="flex flex-col items-center justify-center h-full p-10 text-center" data-testid="multiplier-empty">
      <Handshake weight="duotone" className="text-emerald-400/40" size={48} />
      <div className="font-display text-base text-slate-200 mt-3">
        Procureo is a co-pilot. Bring your move.
      </div>
      <p className="text-xs text-slate-400 font-mono max-w-md mt-2 leading-relaxed">
        Propose a Strategic Move — domestic shift, supplier consolidation, contract horizon, volume commitment —
        and Strategic Partner will return Harmony Impact, Amplification Plays, and seed a Joint Reasoning thread.
      </p>
      <button onClick={onAdd}
        className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-sm border border-emerald-500/40 bg-emerald-500/10
                   text-emerald-200 font-mono text-xs tracking-mono-label hover:bg-emerald-500/20">
        <Sparkle weight="bold" size={12} /> propose strategic move
      </button>
    </div>
  );
}

function Skeleton() {
  return <div className="bg-slate-900 border border-slate-800 p-5 space-y-3" data-testid="multiplier-loading">
    <div className="h-7 skeleton w-1/3 rounded-sm" />
    <div className="h-32 skeleton rounded-sm" />
    <div className="h-48 skeleton rounded-sm" />
  </div>;
}
